import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { SUPPORTED_LANGS, LANG_LABELS } from '@/i18n';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const current = i18n.language || 'it';

  const onChange = async (lang) => {
    if (!SUPPORTED_LANGS.includes(lang) || lang === current) return;
    i18n.changeLanguage(lang);
    if (user) {
      try {
        const { data } = await api.put('/auth/me/language', { language: lang });
        if (refreshUser) refreshUser(data);
      } catch (_e) {
        toast.error('Lingua non aggiornata sul server');
      }
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1" data-testid="language-switcher">
        {SUPPORTED_LANGS.map((lg) => (
          <button
            key={lg}
            onClick={() => onChange(lg)}
            data-testid={`lang-btn-${lg}`}
            title={LANG_LABELS[lg].native}
            className={[
              'px-2 py-1 text-xs uppercase font-mono border transition-colors',
              current === lg
                ? 'bg-[#002FA7] text-white border-[#002FA7]'
                : 'border-gray-700 text-gray-300 hover:border-white hover:text-white',
            ].join(' ')}
          >
            {lg}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="language-switcher">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500">
        <Languages className="w-4 h-4" />
        <span>{t('app.select_language')}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {SUPPORTED_LANGS.map((lg) => (
          <button
            key={lg}
            onClick={() => onChange(lg)}
            data-testid={`lang-option-${lg}`}
            className={[
              'flex items-center gap-2 px-3 py-2 border-2 transition-colors text-left',
              current === lg
                ? 'border-[#002FA7] bg-[#002FA7]/10 text-[#002FA7]'
                : 'border-gray-200 hover:border-[#002FA7]/40 text-gray-700',
            ].join(' ')}
          >
            <span className="text-lg">{LANG_LABELS[lg].flag}</span>
            <span className="font-semibold">{LANG_LABELS[lg].native}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
